import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Pencil, Plus } from 'lucide-react';
import { useTechniquesList } from '@/features/techniques/api';
import { Badge } from '@/components/ui/Badge';
import { Card } from '@/components/ui/Card';

export const Route = createFileRoute('/_authed/techniques/')({
  component: TechniquesPage,
});

function TechniquesPage() {
  const { data, isLoading, error } = useTechniquesList();
  const techniques = data?.techniques ?? [];
  const navigate = useNavigate();

  return (
    <div className="p-8">
      <header className="mb-6">
        <h1 className="font-serif text-3xl leading-tight">Techniques</h1>
        {!isLoading && (
          <p className="text-ink-soft text-sm mt-1">
            Aggregated from all recipes — curate the most-cited slugs first.
          </p>
        )}
      </header>

      <Card shadow="sm" className="p-0 overflow-hidden">
        {isLoading ? (
          <div className="p-6 text-ink-soft">Loading…</div>
        ) : error ? (
          <div className="p-6 text-ink-soft">
            {error instanceof Error ? error.message : 'Failed to load techniques.'}
          </div>
        ) : techniques.length === 0 ? (
          <div className="p-6 text-ink-soft">
            No techniques yet — they&rsquo;ll appear here once a recipe references one.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr
                className="text-left text-xs uppercase tracking-wide text-ink-faint border-b"
                style={{ borderColor: 'var(--color-hairline)' }}
              >
                <th className="px-4 py-3">Slug</th>
                <th className="px-4 py-3">Term</th>
                <th className="px-4 py-3 text-right">Appearances</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 w-24"></th>
              </tr>
            </thead>
            <tbody>
              {techniques.map((t) => (
                <tr
                  key={t.id}
                  className="border-b last:border-b-0 hover:bg-cream-deep/60"
                  style={{ borderColor: 'var(--color-hairline)' }}
                >
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs text-ink-soft">{t.id}</span>
                  </td>
                  <td className="px-4 py-3">
                    {t.term != null ? (
                      <span className="font-medium">{t.term}</span>
                    ) : (
                      <span className="text-ink-faint">&mdash;</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right tabular-nums text-ink-soft">
                    {t.appearances}
                  </td>
                  <td className="px-4 py-3">
                    {t.status === 'curated' ? (
                      <Badge tone="sage">Curated</Badge>
                    ) : (
                      <Badge tone="neutral">Draft</Badge>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() =>
                          navigate({
                            to: '/techniques/$id/edit',
                            params: { id: t.id },
                          })
                        }
                        className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-ink-soft hover:bg-honey-pale hover:text-ink"
                        title={t.status === 'curated' ? 'Edit' : 'Curate'}
                      >
                        {t.status === 'curated' ? (
                          <Pencil size={14} />
                        ) : (
                          <Plus size={14} />
                        )}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
